import axios from 'axios'
import * as Sentry from '@sentry/vue'
import i18n from '@/i18n'
import { extractErrorMessage } from '@/utils/errors'
// stores/toast.js no importa api.js → sin ciclo. Aun así, useToastStore() solo
// se invoca dentro de showErrorToast(), cuando Pinia ya está activo.
import { useToastStore } from '@/stores/toast'

const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '/api/v1',
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json'
  }
})

const UNSAFE_METHODS = ['post', 'put', 'patch', 'delete']

function readCsrfCookie() {
  // Anclado a inicio de cadena o separador "; " — sin anclar, una cookie como
  // `xcsrftoken=` o `mycsrftoken=` haría match parcial y devolvería el token
  // equivocado (CSRF fallando en silencio con 403).
  const match = document.cookie.match(/(?:^|;\s*)csrftoken=([^;]*)/)
  return match && match[1] ? match[1] : null
}

// Rutas de auth: no redirigimos ni toasteamos su 401 (login con credenciales
// incorrectas debe pintar el error en el propio formulario, no un bucle a /auth/login).
function isAuthUrl(url) {
  return (url || '').startsWith('/auth/')
}

let csrfPrimePromise = null
async function primeCsrfCookie() {
  if (csrfPrimePromise) return csrfPrimePromise
  csrfPrimePromise = api.get('/auth/csrf').catch(() => {}).finally(() => {
    csrfPrimePromise = null
  })
  return csrfPrimePromise
}

api.interceptors.request.use(async config => {
  const method = (config.method || 'get').toLowerCase()
  if (UNSAFE_METHODS.includes(method)) {
    let token = readCsrfCookie()
    if (!token && config.url !== '/auth/csrf') {
      await primeCsrfCookie()
      token = readCsrfCookie()
    }
    if (token) config.headers['X-CSRFToken'] = token
  } else {
    const token = readCsrfCookie()
    if (token) config.headers['X-CSRFToken'] = token
  }
  return config
})

/**
 * Muestra el error en un toast global.
 *
 * El store se resuelve DENTRO de la función (no a nivel de módulo): api.js se
 * importa muy pronto, antes de `app.use(pinia)`, y `useToastStore()` en top-level
 * reventaría con "no active Pinia". Si aún no hay Pinia activo, degradamos a
 * console.warn en vez de romper la petición.
 */
function showErrorToast(error) {
  try {
    // Solo usamos extractErrorMessage si el backend mandó un `error` legible;
    // si no, cae a axios ("Request failed with status code 500"), que no dice
    // nada al usuario → mensaje genérico traducido.
    const hasBackendMessage = error?.response?.data?.error != null && error.response.data.error !== ''
    const message = hasBackendMessage
      ? extractErrorMessage(error)
      : i18n.global.t('toast.genericError')
    const toast = useToastStore()
    toast.error(message)
  } catch {
    console.warn('toast unavailable for API error', error?.response?.status)
  }
}

api.interceptors.response.use(
  response => response,
  error => {
    // Reporta fallos de servidor a Sentry (sin PII: solo método, url relativa y status).
    const status = error.response?.status
    if (import.meta.env.VITE_SENTRY_DSN && status >= 500) {
      const method = (error.config?.method || 'get').toUpperCase()
      // Sin query string: puede llevar emails/búsquedas (PII).
      const path = (error.config?.url || '').split('?')[0]
      Sentry.captureMessage(`API ${status} ${method} ${path}`, 'error')
    }
    const url = error.config?.url || ''

    if (error.response?.status === 403) {
      const errData = error.response.data?.error || ''
      const errStr = typeof errData === 'string' ? errData : JSON.stringify(errData)
      if (errStr.includes('tier_required')) {
        const requiredMatch = errStr.match(/tier_required:(\w+)/)
        window.dispatchEvent(new CustomEvent('dorilon:upgrade-required', {
          detail: {
            error: errStr,
            required: requiredMatch ? requiredMatch[1] : 'pro',
          },
        }))
        // El modal de upgrade ya informa al usuario: no dupliques con un toast.
        return Promise.reject(error)
      }
    }

    // Sesión caducada → volver al login. Nunca en /auth/* (evita bucle infinito
    // cuando el propio login responde 401 por credenciales incorrectas).
    if (status === 401 && !isAuthUrl(url)) {
      if (!window.location.pathname.startsWith('/auth/')) {
        window.location.href = '/auth/login'
      }
      return Promise.reject(error)
    }

    // Toast global para el resto de errores 4xx/5xx.
    // Opt-out por petición: api.get(url, { silent: true }).
    if (status >= 400 && !isAuthUrl(url) && error.config?.silent !== true) {
      showErrorToast(error)
    }

    return Promise.reject(error)
  }
)

// Inject X-Project-Id header for all project-scoped endpoints
api.interceptors.request.use(config => {
  // Skip endpoints that don't need project context
  const url = config.url || ''
  if (url.startsWith('/auth/') || url.startsWith('/me') || url.startsWith('/projects')) {
    return config
  }

  // Read activeProjectId from localStorage (avoid coupling to Pinia store here)
  const projectId = localStorage.getItem('dorilon.activeProjectId')
  if (projectId) {
    config.headers['X-Project-Id'] = projectId
  }
  return config
})

export default api
